import React, { ChangeEvent } from 'react';

import { BareEventPropertyInfo, EventTemplate } from '../../shared/types/eventTemplate';
import { StaffAllocationsPanel } from '.';

import { BrandLevel, BrandAttribute } from '../../shared/types/brand';
import { StaffTemplate } from '../../shared/types/staffTemplate';
import { StaffAllocation } from '../../shared/types/staffAllocation';
import { Property } from '@go-shared/types/property';
import { PartialBy } from '@go-shared/types/typeUtils';

import './EventTemplateForm.scss';
import { toast } from './UIkit/Toast/Toast';
import {
  Wizard,
  FormGroup,
  Input,
  TextInput,
  Checkbox,
  Header,
  RadioButtonGroup,
  RadioButton,
  InlineIcon,
  Alert,
  InputElement,
} from '@spark-labs/avocado-ui';
import { EVENT_DISPLAY_MAP, isValidEventDisplayOption } from '@go-shared/lib/event';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
// tslint:disable-next-line
import { CopyToClipboardButton } from './CopyToClipboard';
import {
  BubbleAlert,
  BubbleButton,
  BubbleFormGroup,
  BubbleImageSelector,
  BubbleTypeahead,
} from './BubbleUI';
import { ServerInfo } from '@go-shared/types/info';
import { BubbleTypeaheadOption, BubbleTypeaheadValue } from './BubbleUI/BubbleTypeahead';
import { mapBy } from '@go-shared/lib/functional';
import uuid from 'uuid';
import {
  canManageItemPermissionLevel,
  canManageLibraries,
  canModifyMultiPropertyItem,
} from '@go-shared/lib/permissions';
import { User } from '@go-shared/types/user';
import McmVisibilityTable from './McmVisibilityTable';
import AudienceVisibilityTable from './AudienceVisibilityTable';
import { CallToActions } from './BasicForms/CallToActions';
import { DailyProgramCta } from '@go-shared/types/dailyProgram';

export type PartialStaffAllocation = PartialBy<StaffAllocation, 'template'>;

export interface EventTemplateFormProps {
  mode: 'create' | 'edit';
  user: User;
  serverInfo: ServerInfo;
  eventTemplate: EventTemplate;
  staffTemplates: StaffTemplate[];
  allProperties: Property[];
  property: Property;
  hasTranslations: boolean;
  onSubmit?: (eventTemplate: EventTemplate, action?: 'promote' | 'demote', files?: File[]) => void;
  onArchive?: (eventTemplate: EventTemplate) => void;
  onClone?: (eventTemplate: EventTemplate) => void;
  onEditedEventTemplateChange: (editedEventTemplate: EventTemplate) => void;
}

export interface EventTemplateFormState {
  editedEventTemplate: EventTemplate;
  propertyAccessList: Array<BubbleTypeaheadValue<BareEventPropertyInfo>>;
  // These maps are used for performance reasons. When a checkbox changes, the
  // corresponding _id is looked up in the map and updated. When the form is
  // submitted, these maps are applied to the event template
  levelMap: { [id: string]: boolean };
  attributeMap: { [id: string]: boolean };
  // wizard state
  currentPage: number;
  saving: boolean;
  uploadQueue: File[];
}

export default class EventTemplateForm extends React.Component<
  EventTemplateFormProps,
  EventTemplateFormState
> {
  public readonly state: EventTemplateFormState = {
    editedEventTemplate: this.props.eventTemplate,
    propertyAccessList: [],
    levelMap: {},
    attributeMap: {},
    currentPage: 0,
    saving: false,
    uploadQueue: [],
  };

  constructor(props: EventTemplateFormProps) {
    super(props);

    let { eventTemplate } = props;

    const propertiesDict = mapBy(this.props.allProperties, '_id');

    this.state.propertyAccessList = this.props.eventTemplate.properties
      .filter(p => propertiesDict[p.property]?.active)
      .map(p => ({
        key: p.property,
        refKey: p.property,
        item: {
          property: p.property,
          defaultStaffAllocations: p.defaultStaffAllocations,
        },
      }));
    eventTemplate.brand.levels.forEach(level => {
      this.state.levelMap[level._id] =
        eventTemplate.levels.findIndex(t => t._id === level._id) !== -1;
    });
    eventTemplate.brand.attributes.forEach(attr => {
      this.state.attributeMap[attr._id] =
        eventTemplate.attributes.findIndex(t => t._id === attr._id) !== -1;
    });
  }

  public render() {
    let event = this.state.editedEventTemplate;

    // FIXME: We probably don't need to calculate this at every render (plus, decide what to do with
    // the whole props vs state for editedEventTemplate)
    let propertyInformation = this.state.editedEventTemplate.properties.find(
      p => p.property === this.props.property._id
    );
    let bareDefaultStaffAllocations = propertyInformation
      ? propertyInformation.defaultStaffAllocations
      : [];

    let defaultStaffAllocations: PartialStaffAllocation[] = [];
    bareDefaultStaffAllocations.forEach(bsa => {
      let staffTemplate = this.props.staffTemplates.find(st => st._id === bsa.template);
      defaultStaffAllocations.push({ ...bsa, template: staffTemplate });
    });

    let allowHomecomingEvents = getPropertyConfiguration(
      this.props.property.type,
      'allowHomecomingEvents'
    );

    let allowAllAboardEvents = getPropertyConfiguration(
      this.props.property.type,
      'allowAllAboardEvents'
    );

    let mode = this.props.mode;
    const propertyOpts: Array<BubbleTypeaheadOption<BareEventPropertyInfo>> =
      this.props.allProperties
        .filter(p => p.active)
        .map(p => ({
          key: p._id,
          item: {
            property: p._id,
            defaultStaffAllocations:
              this.state.editedEventTemplate.properties.find(p2 => p2.property === p._id)
                ?.defaultStaffAllocations ?? [],
          },
        }));

    const propertiesDict = mapBy(this.props.allProperties, '_id');

    return (
      <div className="EventTemplateForm border-t border-grey mt-6" style={{ width: '930px' }}>
        {this.permissions().isLocked && (
          <BubbleAlert className="rounded-0-i" variant="ocean">
            <span className="font-semibold">This event cannot be modified.</span> It may be a brand
            level event or you lack library permissions.
          </BubbleAlert>
        )}
        <div className="pt-2 p-4">
          <div className="flex items-center justify-end">
            {this.permissions().canArchive && (
              <BubbleButton
                className="ml-2"
                variant="dirt"
                onClick={this.handleOnArchive}
                icon="box-archive"
              >
                {this.props.eventTemplate.archived ? 'Unarchive Template' : 'Archive Template'}
              </BubbleButton>
            )}
            {this.permissions().canDuplicate && (
              <BubbleButton className="ml-2" variant="navy" onClick={this.handleOnClone}>
                <InlineIcon className="p-0-i mr-2" name="copy" />
                Duplicate
              </BubbleButton>
            )}
          </div>
          <Wizard
            currentPage={this.state.currentPage}
            onCurrentPageChange={this.handleCurrentPageChange}
            onSubmit={() => this.handleFormSubmit(undefined, this.state.uploadQueue)}
          >
            <div>
              <div className="flex">
                <div className="w-1/2 p-4">
                  <BubbleFormGroup label="Images" className="mb-3">
                    <BubbleImageSelector
                      maxItems={3}
                      value={event.images}
                      onValue={images =>
                        this.updateEditedEventTemplate({
                          ...this.state.editedEventTemplate,
                          images,
                        })
                      }
                    />
                  </BubbleFormGroup>
                  {!this.permissions().isBrandLevel && (
                    <BubbleFormGroup
                      label="Properties"
                      className="mb-3"
                      labelFn={props => (
                        <div className="flex items-end">
                          <div className="mr-auto">{props.label}</div>
                          {this.permissions().canEditAccess && (
                            <BubbleButton
                              variant="ocean"
                              inverse
                              className="p-1-i"
                              onClick={() =>
                                this.setState({
                                  propertyAccessList: propertyOpts.map(opt => ({
                                    key: uuid(),
                                    refKey: opt.key,
                                    item: opt.item,
                                  })),
                                })
                              }
                            >
                              Add All Properties
                            </BubbleButton>
                          )}
                        </div>
                      )}
                    >
                      <BubbleTypeahead
                        block
                        labelExtractor={el => propertiesDict[el.property].name}
                        placeholder="Add additional property..."
                        menuClassName="max-h-80 overflow-y-auto"
                        readOnly={!this.permissions().canEditAccess}
                        value={this.state.propertyAccessList}
                        onClearAll={this.handleOnClearAllPropFromAccess}
                        options={propertyOpts}
                        onAdd={this.handleOnAddPropToAccess}
                        onRemove={this.handleOnDeletePropFromAccess}
                      />
                    </BubbleFormGroup>
                  )}
                  <FormGroup>
                    <Input
                      name="title"
                      label="Title"
                      onChange={this.handleInputChange}
                      value={event.title}
                    />
                    {event._id.trim().length > 0 && (
                      <CopyToClipboardButton copyText={event._id} label="Copy ID" />
                    )}
                  </FormGroup>
                  <FormGroup>
                    <TextInput
                      name="description"
                      label="Description"
                      onChange={this.handleInputChange}
                      value={event.description}
                      rows={5}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="defaultDuration"
                      label="Default Duration (minutes)"
                      value={event.defaultDuration}
                      onChange={this.handleInputChange}
                    />
                  </FormGroup>
                  <div className="flex flex-col gap-3">
                    {this.props.hasTranslations && (
                      <Checkbox
                        onChange={this.handleNotNativeChange}
                        label="Foreign Language"
                        checked={!!event.notNative}
                      />
                    )}
                    <div className="flex">
                      <fieldset disabled={mode === 'edit'}>
                        <Checkbox
                          onChange={this.handleInventoriedChange}
                          label="Inventoried"
                          checked={!!event.inventoried}
                        />
                      </fieldset>
                      {event.inventoried && (
                        <div className="flex-grow -mt-1 ml-4">
                          <Input
                            name="initialInventory"
                            type="number"
                            label="Initial Event Inventory"
                            value={event.initialInventory}
                            onChange={this.handleInputChange}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex">
                      <Checkbox
                        onChange={this.handlePriceChange}
                        label="Priced"
                        checked={!!event.priced}
                      />
                      {event.priced && (
                        <div className="flex-grow -mt-1 ml-4">
                          <Input
                            name="initialPrice"
                            label="Initial Event Price"
                            value={event.initialPrice}
                            onChange={this.handleInputChange}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="w-1/2 p-4">
                  <FormGroup>
                    <Header size="sm">Segment</Header>
                    <div className="flex flex-wrap">
                      <RadioButtonGroup
                        name="segment"
                        groupValue={event.segment ? event.segment._id : 'unassigned'}
                        onChange={this.handleSegmentChange}
                      >
                        {groupProps => (
                          <>
                            <span className="w-1/2 my-1">
                              <RadioButton value="unassigned" label="Unassigned" {...groupProps} />
                            </span>
                            {event.brand.segments.map((s, i) => (
                              <span key={i} className="w-1/2 my-1">
                                <RadioButton value={s._id} label={s.name} {...groupProps} />
                              </span>
                            ))}
                          </>
                        )}
                      </RadioButtonGroup>
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Header size="sm">Attributes</Header>
                    <div className="flex flex-wrap bg-grey-lighter p-1">
                      {event.brand.attributes.map((a, i) => (
                        <span key={i} className="w-1/2 my-1">
                          <Checkbox
                            onChange={this.handleAttributeChange}
                            value={a._id}
                            label={a.name}
                            checked={this.state.attributeMap[a._id]}
                          />
                        </span>
                      ))}
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Header size="sm">Levels</Header>
                    <div className="flex flex-wrap">
                      {event.brand.levels.map((l, i) => (
                        <span key={i} className="w-1/2 my-1">
                          <Checkbox
                            onChange={this.handleLevelChange}
                            value={l._id}
                            label={l.name}
                            checked={this.state.levelMap[l._id]}
                          />
                        </span>
                      ))}
                    </div>
                  </FormGroup>
                </div>
              </div>
              <div className="p-4">
                <div>
                  <Header size="sm">Display Options </Header>
                  <FormGroup className="display-options bg-grey-lighter p-4 flex">
                    <div className="w-1/2 flex flex-col">
                      <RadioButtonGroup
                        name="interactiveFeedDisplay"
                        onChange={this.handleInteractiveFeedDisplayChange}
                        groupValue={event.interactiveFeedDisplay}
                      >
                        {groupProps => (
                          <>
                            <RadioButton
                              label={EVENT_DISPLAY_MAP['show-finish-time'].label}
                              value={'show-finish-time'}
                              checked={
                                !event.interactiveFeedDisplay ||
                                event.interactiveFeedDisplay === 'show-finish-time'
                              }
                              {...groupProps}
                            />
                            <RadioButton
                              label={EVENT_DISPLAY_MAP['do-not-show-finish-time'].label}
                              value={'do-not-show-finish-time'}
                              {...groupProps}
                            />
                            <RadioButton
                              value={'show-as-24/7'}
                              label={EVENT_DISPLAY_MAP['show-as-24/7'].label}
                              {...groupProps}
                            />
                            <RadioButton
                              label={EVENT_DISPLAY_MAP['ends-late'].label}
                              value={'ends-late'}
                              {...groupProps}
                            />
                            <RadioButton
                              label={EVENT_DISPLAY_MAP['override-start-time'].label}
                              value={'override-start-time'}
                              {...groupProps}
                            />
                          </>
                        )}
                      </RadioButtonGroup>
                    </div>

                    <div className="w-1/2 flex flex-col">
                      <Checkbox
                        onChange={this.handleCrewOnlyChange}
                        value="crewOnly"
                        label="Internal Only"
                        checked={!!event.crewOnly}
                      />
                      <Checkbox
                        onChange={this.handleSimpleChange}
                        value="simple"
                        label="Simple Event"
                        checked={!!event.simple}
                      />
                      {allowHomecomingEvents && (
                        <Checkbox
                          onChange={this.handleHomecomingChange}
                          value="homecoming"
                          label="Homecoming"
                          checked={!!event.homecoming}
                        />
                      )}
                      {allowAllAboardEvents && (
                        <Checkbox
                          onChange={this.handleAllAboardChange}
                          value="allAboard"
                          label="All Aboard"
                          checked={!!event.allAboard}
                        />
                      )}
                      <Checkbox
                        onChange={this.handleVIPChange}
                        value="vip"
                        label="VIP"
                        checked={!!event.vip}
                      />
                      <Checkbox
                        label="Private (hide from endpoints)"
                        checked={!!event.isPrivate}
                        onChange={this.handlePrivateChange}
                      />
                    </div>
                  </FormGroup>
                </div>
              </div>

              <div className="p-4">
                <Header size="sm">Call to Actions</Header>
                <BubbleFormGroup>
                  <CallToActions
                    callToActions={event.callToActions}
                    onUpdate={this.handleCallToActionChange}
                  />
                </BubbleFormGroup>
              </div>

              <div className="p-4 grid grid-cols-2 gap-4">
                <div>
                  <Header size="sm">Endpoints</Header>
                  <McmVisibilityTable
                    isTemplate
                    sourceMcm={this.state.editedEventTemplate.mcm}
                    parentMcm={undefined}
                    onUpdateMcm={newMcm =>
                      this.setState({
                        editedEventTemplate: { ...this.state.editedEventTemplate, mcm: newMcm },
                      })
                    }
                  />
                </div>
                <div>
                  <Header size="sm">Audience</Header>
                  <AudienceVisibilityTable
                    isTemplate
                    sourceAud={this.state.editedEventTemplate.audience}
                    parentAud={undefined}
                    onUpdateAudience={newAud =>
                      this.setState({
                        editedEventTemplate: {
                          ...this.state.editedEventTemplate,
                          audience: newAud,
                        },
                      })
                    }
                  />
                </div>
              </div>
            </div>
            <div>
              <StaffAllocationsPanel
                isLocked={this.permissions().isLocked}
                eventDuration={this.state.editedEventTemplate.defaultDuration}
                staffAllocations={defaultStaffAllocations}
                staffTemplates={this.props.staffTemplates}
                onChange={this.handleStaffAllocationChange}
              />
            </div>
          </Wizard>
        </div>
      </div>
    );
  }

  private permissions = () => {
    let canSubmit =
      !!this.state.editedEventTemplate.title &&
      this.state.propertyAccessList.length > 0 &&
      !this.state.saving;

    let isBrandLevel = this.props.mode === 'edit' && !this.state.editedEventTemplate.source;
    let isMultiLevel =
      this.props.mode === 'edit' &&
      this.state.editedEventTemplate.source &&
      this.state.editedEventTemplate.properties.length > 1;
    let isLocked = false;
    let canArchive = this.props.mode === 'edit';
    let canDuplicate = this.props.mode === 'edit' && canManageLibraries(this.props.user);
    let canEditAccess = canManageItemPermissionLevel(this.props.user, this.props.serverInfo);

    let canPromote = !isBrandLevel && !isMultiLevel && canEditAccess && this.props.mode === 'edit';
    let canDemote = (isBrandLevel || isMultiLevel) && canEditAccess && this.props.mode === 'edit';

    if (this.props.mode === 'edit') {
      isLocked =
        isLocked ||
        !canModifyMultiPropertyItem(
          this.props.user,
          this.props.serverInfo,
          this.props.property._id,
          this.state.editedEventTemplate
        );
      canSubmit = canSubmit && !isLocked;
      canArchive = canArchive && !isLocked;
    }

    return {
      canSubmit,
      canArchive,
      canDuplicate,
      canEditAccess,
      canPromote,
      canDemote,
      isBrandLevel,
      isLocked,
    };
  };

  private handleOnAddPropToAccess = (prop: BubbleTypeaheadValue<BareEventPropertyInfo>) => {
    this.setState({ propertyAccessList: [...this.state.propertyAccessList, prop] });
  };

  private handleOnDeletePropFromAccess = (prop: BubbleTypeaheadValue<BareEventPropertyInfo>) => {
    this.setState({
      propertyAccessList: [...this.state.propertyAccessList].filter(
        p => p.item.property !== prop.item.property
      ),
    });
  };

  private handleOnClearAllPropFromAccess = () => {
    this.setState({
      propertyAccessList: [],
    });
  };

  private handleOnArchive = async () => {
    if (!this.permissions().canSubmit) {
      await toast.warning(
        'Event template editing was not successful. Check the form and try again.'
      );
    } else {
      const yes = await Alert.question(
        `Are you sure you want to ${this.props.eventTemplate.archived ? 'unarchive' : 'archive'} "${
          this.props.eventTemplate.title
        }"?`
      );

      if (yes) {
        this.props.onArchive!({
          ...this.props.eventTemplate,
          archived: !this.props.eventTemplate.archived,
        });
      }
    }
  };

  private handleOnClone = () => {
    if (this.props.onClone) {
      this.props.onClone({
        ...this.props.eventTemplate,
        properties: [
          {
            property: this.props.property._id,
            defaultStaffAllocations:
              this.state.editedEventTemplate.properties.find(
                p2 => p2.property === this.props.property._id
              )?.defaultStaffAllocations ?? [],
          },
        ],
      });
    }
  };

  private handleFormSubmit = async (action?: 'promote' | 'demote', files?: File[]) => {
    try {
      this.setState({ saving: true });

      if (!this.permissions().canSubmit || !this.props.onSubmit) {
        if (!this.state.editedEventTemplate.title) {
          Alert.error('Title field is required').then().catch(console.error);
          return;
        }

        if (this.state.propertyAccessList.length <= 0) {
          Alert.error('Properties is required').then().catch(console.error);
          return;
        }

        await toast.warning(
          'Event template editing was not successful. Check the form and try again.'
        );
        return;
      }

      // Transfer the map information into the eventTemplate
      let levels: BrandLevel[] = [];
      let attributes: BrandAttribute[] = [];

      this.props.eventTemplate.brand.levels.forEach(l => {
        if (this.state.levelMap[l._id]) levels.push(Object.assign({}, l));
      });

      this.props.eventTemplate.brand.attributes.forEach(a => {
        if (this.state.attributeMap[a._id]) attributes.push(Object.assign({}, a));
      });

      const newEventTemplate = { ...this.state.editedEventTemplate, levels, attributes };

      newEventTemplate.properties = this.state.propertyAccessList.map(pal => pal.item);

      this.props.onSubmit(newEventTemplate, action, files);
    } finally {
      this.setState({ saving: false });
    }
  };

  private handleInputChange = (event: ChangeEvent<InputElement>) => {
    let newState = { ...this.state.editedEventTemplate, [event.target.name]: event.target.value };
    this.updateEditedEventTemplate(newState as any);
  };

  private handleLevelChange = (event: ChangeEvent<HTMLInputElement>) => {
    // find the _id in the levels map and update it depending on whether it was
    // checked or unchecked.
    this.setState({
      levelMap: { ...this.state.levelMap, [event.target.value]: event.target.checked },
    });
  };

  private handleSegmentChange = (segmentId: string) => {
    let selectedSegment;

    if (segmentId !== 'unassigned') {
      selectedSegment = this.state.editedEventTemplate.brand.segments.find(
        s => s._id === segmentId
      );

      if (!selectedSegment) {
        // something went wrong: we cannot find the segment id in the brand's segments
        throw new Error(`Could not find segment ${segmentId}`);
      }
    }

    this.setState({
      editedEventTemplate: { ...this.state.editedEventTemplate, segment: selectedSegment },
    });
  };

  private handleAttributeChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      attributeMap: { ...this.state.attributeMap, [event.target.value]: event.target.checked },
    });
  };

  private handleCrewOnlyChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.updateEditedEventTemplate({
      ...this.state.editedEventTemplate,
      crewOnly: event.target.checked,
    });
  };

  private handleInventoriedChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Set non-inventoried events to inventory of 0
    if (!event.target.checked) {
      this.state.editedEventTemplate.initialInventory = 0;
    }

    this.updateEditedEventTemplate({
      ...this.state.editedEventTemplate,
      inventoried: event.target.checked,
    });
  };

  private handlePriceChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Set non-fee events to inventory of 0
    if (!event.target.checked) {
      this.state.editedEventTemplate.initialPrice = 0;
    }

    this.updateEditedEventTemplate({
      ...this.state.editedEventTemplate,
      priced: event.target.checked,
    });
  };

  private handlePrivateChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.updateEditedEventTemplate({
      ...this.state.editedEventTemplate,
      isPrivate: event.target.checked,
    });
  };

  private handleNotNativeChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.updateEditedEventTemplate({
      ...this.state.editedEventTemplate,
      notNative: event.target.checked,
    });
  };

  private handleSimpleChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.updateEditedEventTemplate({
      ...this.state.editedEventTemplate,
      simple: event.target.checked,
    });
  };

  private handleHomecomingChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.updateEditedEventTemplate({
      ...this.state.editedEventTemplate,
      homecoming: event.target.checked,
    });
  };

  private handleAllAboardChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.updateEditedEventTemplate({
      ...this.state.editedEventTemplate,
      allAboard: event.target.checked,
    });
  };

  private handleVIPChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.updateEditedEventTemplate({
      ...this.state.editedEventTemplate,
      vip: event.target.checked,
    });
  };

  private handleInteractiveFeedDisplayChange = (interactiveFeedDisplay: string) => {
    if (interactiveFeedDisplay && !isValidEventDisplayOption(interactiveFeedDisplay)) {
      throw new Error('Wrong interactive feed option');
    }

    let newState = { ...this.state.editedEventTemplate, interactiveFeedDisplay };
    this.updateEditedEventTemplate(newState as any);
  };

  private handleCurrentPageChange = (newPage: number) => {
    this.setState({ currentPage: newPage });
  };

  private handleStaffAllocationChange = (staffAllocations: PartialStaffAllocation[]) => {
    let propertyInformation = this.state.editedEventTemplate.properties.find(
      p => p.property === this.props.property._id
    );
    if (!propertyInformation) return;

    let bareStaffAllocations = staffAllocations.map(sa => ({
      ...sa,
      template: sa.template ? sa.template._id : '',
    }));

    propertyInformation.defaultStaffAllocations = bareStaffAllocations;
    this.updateEditedEventTemplate(this.state.editedEventTemplate);
  };

  private handleCallToActionChange = (callToActions: DailyProgramCta[]) => {
    this.updateEditedEventTemplate({
      ...this.state.editedEventTemplate,
      callToActions,
    });
  };

  /**
   * Sets the form state to `unsaved` so we can warn the user if he wants to
   * exit without saving
   */
  private updateEditedEventTemplate = (eventTemplate: EventTemplate) => {
    this.props.onEditedEventTemplateChange(eventTemplate);
    this.setState({ editedEventTemplate: eventTemplate });
  };
}
